import { LiveKitClientInterface } from "@share/live-kit/interfaces/LiveKitClientInterface";
import {
    AudioCaptureOptions, ConnectionError,
    createLocalAudioTrack,
    createLocalVideoTrack,
    LocalAudioTrack, LocalParticipant,
    LocalTrack,
    LocalVideoTrack, Participant, RemoteAudioTrack, RemoteVideoTrack,
    Room, setLogLevel,
    Track,
    VideoCaptureOptions,
    VideoPresets
} from "livekit-client";
import { LiveKitTracksManagerInterface } from "@share/live-kit/interfaces/LiveKitTracksManagerInterface";
import { UserRoleType } from "@share/web-conferences/enums/UserRoleType";
import { LiveKitCredentials } from "@api/models/web-conferences/LiveKitCredentials";
import { DevicesInfo } from "@share/web-conferences/types/DevicesInfo";
import { WebConferenceTypes } from "@share/web-conferences/enums/WebConferenceTypes";
import { StoreHelper } from "@share/web-conferences/helpers/StoreHelper";
import Vue from "vue";
import { getDevicesFromStorage } from "@share/web-conferences/helpers/LocalStorageHelper";
import { DeviceStatesPromisesQueue } from "@share/web-conferences/helpers/DeviceStatesPromisesQueue";
import Source = Track.Source;
import { loggerInstance } from "@share/live-kit/logger";

export class LiveKitClient implements LiveKitClientInterface {
    joined = false;
    micIsOn = true;
    videoIsOn = true;
    room: Room;
    uid = '';
    userRole: UserRoleType = UserRoleType.Audience;
    tracksStatesQueues: Record<"video" | "audio", DeviceStatesPromisesQueue> = {
        "video": new DeviceStatesPromisesQueue(this.switchVideoTrack.bind(this), 500),
        "audio": new DeviceStatesPromisesQueue(this.switchAudioTrack.bind(this))
    };
    encoder: TextEncoder = new TextEncoder();
    connectAttempt = 0;

    constructor(
        public readonly tracksManager: LiveKitTracksManagerInterface,
        // public volumesManager: AgoraVolumesManagerInterface,
        // public networkQuality: AgoraNetworkQualityInterface,
        private readonly type: WebConferenceTypes,
        private readonly storeHelper: StoreHelper
    ) {
        this.room = Vue.observable(new Room({
            adaptiveStream: true,
            publishDefaults: {
                simulcast: true,
                videoCodec: "vp8"
            },
            videoCaptureDefaults: {
                resolution: VideoPresets.h720.resolution
            }
        }));

        const deviceInfo = getDevicesFromStorage();
        this.setDevicesData(deviceInfo);
    }

    async changeAudioDevice(deviceId: string): Promise<void> {
        await this.room.switchActiveDevice('audioinput', deviceId);
    }

    async changeVideoDevice(deviceId: string): Promise<void> {
        console.log('devvice', deviceId);
        await this.room.switchActiveDevice("videoinput", deviceId);
    }

    async changeRoleToAudience(): Promise<void> {
        try {
            if (this.type == WebConferenceTypes.Meeting) {
                await this.leave();
                return;
            }

            const localTracks = this.tracksManager.getLocalUserTracks();
            this.room.localParticipant.unpublishTracks([localTracks.audio as LocalTrack, localTracks.video as LocalTrack]);
            await this.setClientRole(UserRoleType.Audience);
            await this.tracksManager.removeLocal();
            await this.room.localParticipant.setScreenShareEnabled(false);
        }
        catch (e) {
            this.storeHelper.store.dispatch('reportException', e);
        }
    }

    async changeRoleToHost(createTracks = true): Promise<void> {
        try {
            await this.setClientRole(UserRoleType.Presenter);
            createTracks && await this.createLocalTracks();
        }
        catch (e) {
            this.storeHelper.store.dispatch('reportException', e);
        }
    }

    clean(removeListeners?: boolean): void {
        this.room.disconnect(true);
        removeListeners && this.room.removeAllListeners();
        this.tracksManager.clean();
        // this.remoteUsers.clean();
        // this.volumesManager.clean();
        // this.client.stopProxyServer();
        this.joined = false;
        // this.isUsingProxy = false;
        // this.networkQuality.unsubscribe();
    }

    async createAudioTrack(config: AudioCaptureOptions = {
        echoCancellation: true,
        noiseSuppression: {
            ideal: true,
        }
    }): Promise<LocalAudioTrack | null> {
        return await createLocalAudioTrack(config);
    }

    async createVideoTrack(_: VideoCaptureOptions = {

    }): Promise<LocalVideoTrack | null> {
        return await createLocalVideoTrack();
    }

    async createLocalTracks(): Promise<void> {
        try {
            const devices = getDevicesFromStorage();

            if (devices.videoDeviceId) {
                await this.changeVideoDevice(devices.videoDeviceId);
            }

            if (devices.audioDeviceId) {
                await this.changeAudioDevice(devices.audioDeviceId);
            }
            //
            // let [localAudioTrack, localVideoTrack] = await Promise.all([
            //     this.micIsOn ? this.createAudioTrack() : null,
            //     this.videoIsOn ? this.createVideoTrack() : null
            // ]);

            await this.publishLocalTracks();

            // await this.tracksManager.add({
            //     uid: this.uid,
            //     audio: localAudioTrack ?? undefined,
            //     video: localVideoTrack ?? undefined,
            //     isLocal: true
            // });
        }
        catch (e) {
            this.storeHelper.store.dispatch('reportException', e);
        }
    }

    async join(userRole: UserRoleType, credentials: LiveKitCredentials): Promise<boolean> {

        try {
            console.log('%cjoining', 'font-size:30px', this.connectAttempt);
            loggerInstance.log('joining:' + this.connectAttempt);

            this.setClientRole(userRole);
            setLogLevel("debug");
            await this.room.connect(credentials.serverUrl, credentials.jwt, {
                autoSubscribe: true
            });

            this.uid = this.room.localParticipant.identity;

            console.log('joined to room', this.room.name, this.uid);

            // await this.remoteUsers.subscribe(this.client, this.tracksManager);
            // await this.volumesManager.start(this.client);
            // await this.networkQuality.subscribe(this.client);

            this.joined = true;
            this.connectAttempt = 0;

            return true;
        }
        catch (e) {
            // if (e.code && ['CAN_NOT_GET_PROXY_SERVER', 'NETWORK_ERROR', 'WS_ABORT'].includes(e.code) && !isClientUsingProxy) {
            //     this.startProxy();
            //     return await this.join(userRole, credentials);
            // }
            if (e instanceof ConnectionError && e.code == 1 && e.message == 'could not connect after timeout' && this.connectAttempt < 2) {
                this.connectAttempt++;
                return await this.join(userRole, credentials);
            }

            this.connectAttempt = 0;

            console.log('failed', e);
            loggerInstance.log('failed');
            loggerInstance.log(e.message);
            this.storeHelper.store.dispatch('reportError', e);
        }
        return false;
    }

    async leave(): Promise<void> {
        if (this.joined) {
            return;
        }

        this.connectAttempt = 0;

        console.log('%cleaving', 'font-size: 30px');
        try {
            await this.tracksManager.release();
        } catch (e) {
            this.storeHelper.store.dispatch('reportException', e);
        }

        await this.room.disconnect(true);

        // this.client.stopProxyServer();

        this.joined = false;
    }

    async publishLocalTracks(): Promise<void> {
        console.log('publishing local tracks');
        try {
            await Promise.all([
                this.micIsOn ? this.room.localParticipant.setMicrophoneEnabled(true) : null,
                this.videoIsOn ? this.room.localParticipant.setCameraEnabled(true) : null,
            ]);
        }
        catch (e) {
            this.storeHelper.store.dispatch('reportException', e);
            console.log('publishing local tracks failed');
        }
    }

    setDevicesData(data: DevicesInfo): void {
        Object.assign(this, data);
        this.tracksStatesQueues.audio.previousState = this.micIsOn;
        this.tracksStatesQueues.video.previousState = this.videoIsOn;
    }

    async switchMic(state: boolean): Promise<void> {
        try {
            this.tracksManager.changeAudioState(this.uid, state);
            this.micIsOn = state;
            this.tracksStatesQueues.audio.add(state);
        }
        catch (e) {
            this.storeHelper.store.dispatch('reportException', e);
        }
    }

    async switchVideo(state: boolean): Promise<void> {
        try {
            this.tracksManager.changeVideoState(this.uid, state);
            this.videoIsOn = state;
            this.tracksStatesQueues.video.add(state);
        }
        catch (e) {
            this.storeHelper.store.dispatch('reportException', e);
        }
    }

    async switchVideoTrack(state: boolean) {
        await this.room.localParticipant.setCameraEnabled(state);
    }

    async switchAudioTrack(state: boolean) {
        await this.room.localParticipant.setMicrophoneEnabled(state);
    }

    setClientRole(role: UserRoleType) {
        this.userRole = role;
    }

    async updateParticipant(participant: Participant, isScreenShare = false) {
        const videoTrack = participant.getTrack(Source.Camera)?.track;
        const audioTrack = participant.getTrack(Source.Microphone)?.track;

        this.tracksManager.update({
            uid: participant.identity,
            video: videoTrack as RemoteVideoTrack,
            audio: audioTrack as RemoteAudioTrack,
            isLocal: participant instanceof LocalParticipant,
            isScreenShare: isScreenShare
        });
    }
}
